import React, { useMemo, useState } from "react";
import { formatPriceNOK } from "../../../utilities/conversions";
import { AlertTriangle, Clock4, PhoneIncoming, Wallet } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

type PerformanceAndKpiProps = {
    callsThisMonth: number;
    avgCallMinutes: number;
    totalRevenue: number;
    outstanding: number;
    revenueSeries: any[];
}
export default function PerformanceAndKpi({callsThisMonth, avgCallMinutes, totalRevenue, outstanding, revenueSeries} : PerformanceAndKpiProps) {
    // --- Dummy datasource (replace with Chamcore APIs) ---
    // const revenueSeries = [
    //     { day: "01", value: 1200 },
    //     { day: "05", value: 900 },
    //     { day: "10", value: 1700 },
    //     { day: "15", value: 2200 },
    //     { day: "20", value: 1850 },
    //     { day: "25", value: 2600 },
    //     { day: "30", value: 3100 },
    // ];

    const initialInvoices = [
        { id: "INV-1043", date: "2025-05-04", customer: "Nordic Elektro AS", amount: 14500, vat: 25, status: "Pending" },
        { id: "INV-1042", date: "2025-05-02", customer: "Hjem & Hage DA", amount: 8200, vat: 25, status: "Paid" },
        { id: "INV-1041", date: "2025-04-28", customer: "Trygg Regnskap", amount: 3600, vat: 25, status: "Overdue" },
    ];

    
    const [invoices, setInvoices] = useState(initialInvoices);
    const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
    const [sendEmail, setSendEmail] = useState(true);

    // New invoice form state
    const [form, setForm] = useState({
        customer: "",
        email: "",
        org: "",
        description: "",
        qty: 1,
        unitPrice: 1000,
        vat: 25,
        dueDays: 14,
        fastpris: false,
    });

    const subtotal = useMemo(() => (form.fastpris ? form.unitPrice : form.unitPrice * form.qty), [form]);
    const vatAmount = useMemo(() => (subtotal * form.vat) / 100, [subtotal, form.vat]);
    const total = useMemo(() => subtotal + vatAmount, [subtotal, vatAmount]);

    const kpi = useMemo(() => {
        const totalPaid = invoices.filter(i => i.status === "Paid").reduce((a, b) => a + b.amount, 0);
        // const outstanding = invoices.filter(i => i.status !== "Paid").reduce((a, b) => a + b.amount, 0);
        return {
            appointmentsToday: 3,
            awaitingPayout: 1299,
            paidOut: 899,
            callsThisMonth: callsThisMonth,
            avgCallMinutes: avgCallMinutes,
            totalRevenue: totalRevenue,
            outstanding,
            paidToday: 2150,
        };
    }, [invoices]);

    function handleCreateInvoice() {
        const newId = `INV-${(1043 + invoices.length + 1)}`;
        const newInvoice = {
            id: newId,
            date: new Date().toISOString().slice(0, 10),
            customer: form.customer || "Uspesifisert kunde",
            amount: Math.round(total),
            vat: form.vat,
            status: "Pending" as const,
        };

        // TODO: POST to Chamcore API
        // await fetch("/api/invoices", { method: "POST", body: JSON.stringify({...}) })

        setInvoices([newInvoice, ...invoices]);
        setOpenInvoiceModal(false);
    }

    function Stat({ label, value, icon }: { label: string; value: any; icon: React.ReactNode }) {
        return (
            <div className="rounded-lg border p-4 bg-white shadow-sm flex items-center gap-3">
                <div className="p-2 rounded-full bg-purple-100 text-purple-700">
                    {icon}
                </div>
                <div>
                    <div className="text-xs text-slate-600">{label}</div>
                    <div className="text-xl font-semibold">{value}</div>
                </div>
            </div>
        );
    }


    return (
        <>
            <div className="grid grid-cols-[1fr_auto_auto] gap-2 mb-3 mr-auto">
                <h3 className="font-bold text-purple-900">Performance & KPI</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <Stat label="Fullførte samtaler (mnd)" value={kpi.callsThisMonth} icon={<PhoneIncoming className="h-4 w-4" />} />
                <Stat label="Gj.snitt varighet" value={`${kpi.avgCallMinutes} min`} icon={<Clock4 className="h-4 w-4" />} />
                <Stat label="Total omsetning" value={formatPriceNOK(kpi.totalRevenue)} icon={<Wallet className="h-4 w-4" />} />
                <Stat label="Utestående" value={formatPriceNOK(kpi.outstanding)} icon={<AlertTriangle className="h-4 w-4" />} />
            </div>
            <div className="mt-6 h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueSeries}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="day" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="value" strokeWidth={3} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </>
    )

}